import React from 'react';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { BaseServiceComponentProps } from '@/pages/benchmark/types/commom';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { useParams } from 'umi';
import { getCompleteDateValue } from '@/pages/dashboard/utils/utils';
import { DATA_TYPE_OBJECT } from '@home/utils/common';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { getFilterConfig } from '../utils';

export interface IStudioMetricCardServiceProps
  extends BaseServiceComponentProps {
  metric: string[];
  trendMetrics: string[];
  gameCodes: string[];
  studioCode: string;
  dataSource: string;
  breakdownMetrics: string;
  isByDay?: boolean;
  [key: string]: any;
}

// 定义 Filters 类型
interface FiltersType {
  [key: string]: string[] | Array<{ key: string; filter_list: any[]; }>;
}

export interface ParamsType {
  game_codes: string[];
  game_code?: string;
  studio_code: string;
  data_source: string;
  period: any;
  start_time: string;
  end_time: string;
  last_date?: string;
  metrics?: string[];
  trend_metrics?: string[];
  is_confirm: boolean;
  filters: FiltersType;
  breakdown_dimension?: string;
  breakdown_metrics?: string;
  card_type?: string;
  by_day?: boolean;
  system?: string;
  logical_flag?: string;
}

export default function StudioMetricCardService(
  props: IStudioMetricCardServiceProps,
) {
  const {
    style,
    className,
    refreshKey,
    interval,
    dataKey = 'data',
    pageType,
    api,
    children,
    filter,
    metric,
    trendMetrics,
    gameCodes,
    dataSource,
    breakdownMetrics,
    isByDay = false,
    formatData,
    extraParams = {},
    filterConfig,
    ...resetProps
  } = props || {};

  const { studioId, gameCode } = useParams() || ({} as any);
  const formatDataFunc = (data: any) => {
    if (formatData) return stringToFunction(formatData)(data);
    return data;
  };

  return (
    <div className={className} style={style}>
      <Service
        api={api}
        formatData={formatDataFunc}
        data={{ ...props.data }}
        dataKey={dataKey}
        refreshKey={refreshKey}
        interval={interval}
        filterLoading={false}
        filterVal={filter}
        beforeRequest={(data) => {
          const { filterVal } = data;
          const {
            start_time,
            end_time,
            date_type,
            isSingle,
            payType,
            selectorList,
          } = filterVal || {};
          let startDate = dateTimeFormat(start_time, 'day');
          const rangeTime = [start_time, end_time];
          const time_type = DATA_TYPE_OBJECT[date_type];
          // 补齐逻辑
          if (isSingle) {
            const rangeTimeArr = getCompleteDateValue(rangeTime, time_type);
            startDate = dateTimeFormat(rangeTimeArr[0], 'day');
          }

          let params: ParamsType = {
            studio_code: studioId,
            game_codes: gameCodes,
            data_source: dataSource,
            period: date_type,
            start_time: startDate,
            end_time: dateTimeFormat(end_time, 'day'),
            metrics: metric,
            trend_metrics: trendMetrics,
            is_confirm: !isSingle,
            logical_flag: 'show_full_period_data',
            filters: {
              zone_list: ['255'],
              market_list: ['255'],
              channel_list: ['255'],
              os_list: ['255'],
              category_list: ['255'],
              product_list: ['255'],
            },
          };
          // 筛选器过滤
          if(filterConfig){
            const processedFilters = getFilterConfig(selectorList, filterConfig);
            params.filters = processedFilters;
          }          
          // 根据 dataKey 更新 params
          if (dataKey === 'metricRank') {
            params = {
              ...params,
              card_type: '1',
              breakdown_dimension: 'os',
              breakdown_metrics: breakdownMetrics,
            };
          }
          if (dataKey === 'metricRankCountry') {
            params = {
              ...params,
              card_type: '1',
              breakdown_dimension: 'country',
              breakdown_metrics: breakdownMetrics,
            };
            delete params.metrics;
            delete params.trend_metrics;
          }
          if (pageType === 'InGameAnalytics') {
            delete params.logical_flag;
            params.game_code = gameCode;
            params.filters = filter?.filters;
            params.last_date = filter?.last_date;
            params.system = 'studio';
            // kpi趋势图
            if (isByDay) {
              params.start_time = dateTimeFormat(start_time, 'day');
              params.by_day = true;
            }
          }
          if (payType) {
            // 增加筛选器参数
            params.filters.custom_filters = [
              {
                key: 'payment_type',
                filter_list: [payType],
              },
            ];
          }
          return { ...params, ...extraParams };
        }}
        trackData={[]}
      >
        {React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...(resetProps || {}),
            filter,
            filterConfig,
          }),
        )}
      </Service>
    </div>
  );
}
